import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { storeLanguage } from '@/lib/languageStorage';
import { SUPPORTED_LANGUAGE_OPTIONS, type SupportedLanguage } from '@/i18n/supportedLanguages';
import { cn } from '@/lib/utils';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateCurrentUserProfileMutation } from '@/hooks/useCurrentUser';

const LANGUAGE_NAME_KEY: Record<SupportedLanguage, string> = {
  en: 'languageName.en',
  pl: 'languageName.pl',
};

export function PreferencesSection() {
  const { t, i18n } = useTranslation('settings');
  const { currentUser, refreshCurrentUser } = useAuth();
  const queryClient = useQueryClient();
  const updateProfileMutation = useUpdateCurrentUserProfileMutation();
  const currentLanguage = currentUser === null ? '' : currentUser.preferredLanguage;
  const isChangingLanguage = updateProfileMutation.isPending;
  const pendingLanguage =
    isChangingLanguage === true && updateProfileMutation.variables !== undefined
      ? updateProfileMutation.variables.preferredLanguage
      : undefined;

  async function selectLanguage(language: SupportedLanguage) {
    if (updateProfileMutation.isPending === true || language === currentLanguage) {
      return;
    }
    try {
      await updateProfileMutation.mutateAsync({ preferredLanguage: language });
      await refreshCurrentUser();
      await i18n.changeLanguage(language);
      storeLanguage(language);
      await queryClient.invalidateQueries();
      showSuccessNotification(t('practitioner.preferences.saved'));
    } catch (updateError) {
      const backendMessage = resolveApiErrorMessage(updateError);
      showErrorNotification(
        backendMessage.length > 0 ? backendMessage : t('practitioner.preferences.saveError')
      );
    }
  }

  return (
    <Card padding="lg">
      <h2 className="text-lg font-bold text-zooindigo">{t('practitioner.preferences.heading')}</h2>
      <div className="mt-6 space-y-2">
        <div className="text-xs font-bold text-zoomuted tracking-wider uppercase">
          {t('practitioner.preferences.appLanguage')}
        </div>
        <div className="inline-flex bg-zoooffwhite border border-zooborder rounded-xl p-1">
          {SUPPORTED_LANGUAGE_OPTIONS.map(function renderLanguageOption(language) {
            const isActive = language === currentLanguage;
            const isSelectingThisLanguage = pendingLanguage === language;
            return (
              <button
                key={language}
                type="button"
                aria-pressed={isActive}
                aria-busy={isSelectingThisLanguage === true ? true : undefined}
                disabled={isChangingLanguage}
                onClick={function chooseLanguage() {
                  selectLanguage(language);
                }}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5',
                  isActive ? 'bg-zooprimary text-zoowhite shadow-sm' : 'text-zoomuted hover:text-zooindigo'
                )}
              >
                {isSelectingThisLanguage === true ? <LoadingSpinner className="w-3.5 h-3.5" /> : null}
                {t(LANGUAGE_NAME_KEY[language])}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-start gap-2 text-[13px] text-zoomuted">
        <Icon name="Bell" />
        <span>{t('practitioner.preferences.notificationsHint')}</span>
      </div>
    </Card>
  );
}
